import { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Small live badge in the header showing Claude API health. Self-contained:
// polls /api/health/llm every 60s and lets the user trigger a manual re-probe.
const LLMHealthIndicator = () => {
  const [health, setHealth] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchHealth = async () => {
      try {
        const res = await axios.get(`${API}/health/llm`, { timeout: 8000 });
        if (!cancelled) setHealth(res.data);
      } catch (err) {
        if (!cancelled) {
          console.warn("Health probe failed:", err?.response?.status || err?.message);
          // Mark as down so the badge stops being stuck on "?" when backend is unreachable
          setHealth({ status: "down" });
        }
      }
    };
    fetchHealth();
    const interval = setInterval(fetchHealth, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const triggerProbe = async () => {
    try {
      await axios.post(`${API}/health/llm/probe`);
      setTimeout(async () => {
        const res = await axios.get(`${API}/health/llm`);
        setHealth(res.data);
      }, 8000);
    } catch (err) {
      console.warn("Manual probe failed:", err?.response?.status || err?.message);
    }
  };

  const status = health?.status || "unknown";
  const color = {
    healthy: "bg-emerald-400",
    slow: "bg-violet-600",
    down: "bg-red-500",
    unconfigured: "bg-zinc-500",
    unknown: "bg-zinc-600",
  }[status];
  const glow = {
    healthy: "shadow-[0_0_8px_#10B981]",
    slow: "shadow-[0_0_8px_#7C3AED]",
    down: "shadow-[0_0_8px_#EF4444]",
    unconfigured: "",
    unknown: "",
  }[status];
  const label = {
    healthy: "AI · LIVE",
    slow: "AI · SLOW",
    down: "AI · DOWN",
    unconfigured: "AI · OFF",
    unknown: "AI · ?",
  }[status];
  const tooltip = health
    ? `Claude API: ${status.toUpperCase()}${health.latency_ms ? ` · ${health.latency_ms}ms` : ""}${health.message ? ` · ${health.message}` : ""}${health.checked_at ? `\nLast checked: ${new Date(health.checked_at).toLocaleTimeString()}` : ""}\n\nClick to re-check now`
    : "Click to check";

  return (
    <button
      onClick={triggerProbe}
      title={tooltip}
      className="inline-flex items-center gap-2 px-2 py-1 hover:bg-white transition-colors"
      data-testid="llm-health-indicator"
    >
      <span className={`w-2 h-2 rounded-full ${color} ${glow} ${status === "healthy" ? "animate-pulse" : ""}`} />
      <span className="hidden sm:inline text-[10px] font-mono-data font-semibold text-zinc-500 tracking-[0.2em]">{label}</span>
    </button>
  );
};

export default LLMHealthIndicator;
